import type {
  AnalysisChatV2Id,
  OrganizationId,
  ReelId,
  ResponseId,
  SurveyId,
} from "@/backend/db/drizzle/types/idTypes";
import type { AgentReportId } from "@/components/analysis/agentic/db_types";
import type { InterviewItemTemplateId } from "@/components/interview/types";
import type {
  RecruitmentId,
  TargetGroupId,
} from "@/components/outreach/panel/types";
import { zodBrandedUuid } from "@/utils/helpers";
import { z } from "zod";

export const MAX_PAGE_TARGETS_PER_REQUEST = 50;

const surveyId = zodBrandedUuid<SurveyId>();
const workspaceId = zodBrandedUuid<OrganizationId>();

const studyPage = <Page extends string>(page: Page) =>
  z.object({ page: z.literal(page), surveyId });

const workspacePage = <Page extends string>(page: Page) =>
  z.object({ page: z.literal(page), orgId: workspaceId });

// IDs rather than names, so a renamed report or study still resolves. Editor tokens and route bodies carry them as text, so they are checked as UUIDs.
// Each variant notes its page URL and the placeholder the agent writes for it.
export const pageTargetSchema = z.discriminatedUnion("page", [
  // URL /p/<link>; agent writes {{/pages/<link>}}
  studyPage("study"),
  // URL /p/<link>/study-guide; agent writes {{/pages/<link>/study-guide}}
  studyPage("study-guide"),
  // URL /p/<link>/study-guide?question=<templateId>; agent writes {{/pages/<link>/study-guide/Q3}}
  studyPage("study-guide-question").extend({
    templateId: zodBrandedUuid<InterviewItemTemplateId>(),
  }),
  // URL /p/<link>/launch; agent writes {{/pages/<link>/launch}}
  studyPage("launch"),
  // URL /p/<link>/outreach; agent writes {{/pages/<link>/recruit}}
  studyPage("recruit"),
  // URL /p/<link>/outreach/tg/<id>; agent writes {{/pages/<link>/recruit/target-groups/<id>}}
  studyPage("target-group").extend({
    targetGroupId: zodBrandedUuid<TargetGroupId>(),
  }),
  // URL /p/<link>/outreach/tg/<id>/review; agent writes {{/pages/<link>/recruit/target-groups/<id>/review}}
  studyPage("target-group-review").extend({
    targetGroupId: zodBrandedUuid<TargetGroupId>(),
  }),
  // URL /p/<link>/outreach/email/<id>; agent writes {{/pages/<link>/recruit/emails/<id>}}
  studyPage("recruitment-email").extend({
    recruitmentId: zodBrandedUuid<RecruitmentId>(),
  }),
  // URL /p/<link>/responses; agent writes {{/pages/<link>/responses}}
  studyPage("responses"),
  // URL /p/<link>/responses/14; agent writes {{/pages/<link>/responses/14}} with the readable response id
  studyPage("response").extend({ responseId: zodBrandedUuid<ResponseId>() }),
  // URL /p/<link>/analysis/report[/<name>]; agent writes {{/pages/<link>/reports}} for the main report or {{/pages/<link>/reports/<name>}}
  studyPage("report").extend({
    /** Null is the study's main report page. */
    reportId: zodBrandedUuid<AgentReportId>().nullable(),
  }),
  // URL /p/<link>/analysis/question-analysis; agent writes {{/pages/<link>/question-analysis}}
  studyPage("question-analysis"),
  // URL /p/<link>/analysis/question-analysis?question=<templateId>; agent writes {{/pages/<link>/question-analysis/Q3}}
  studyPage("question-analysis-question").extend({
    templateId: zodBrandedUuid<InterviewItemTemplateId>(),
  }),
  // URL /p/<link>/analysis/reels; agent writes {{/pages/<link>/clips}}
  studyPage("clips"),
  // URL /p/<link>/reels/<reelId>; agent writes {{/pages/<link>/reels/<run>/<reel>}} for the reel /<run>/<link>/reels/<reel>
  studyPage("reel").extend({ reelId: zodBrandedUuid<ReelId>() }),
  // URL /dashboard; agent writes {{/pages/dashboard}}
  workspacePage("dashboard"),
  // URL /reels/<reelId>; agent writes {{/pages/reels/<run>/<reel>}} for the reel /<run>/reels/<reel>
  workspacePage("org-reel").extend({ reelId: zodBrandedUuid<ReelId>() }),
  // URL /chat/<chatId>; agent writes {{/pages/chats/<id>}}
  workspacePage("chat").extend({ chatId: zodBrandedUuid<AnalysisChatV2Id>() }),
  // URL /workspace; agent writes {{/pages/workspace}}
  workspacePage("workspace"),
  // URL /billing; agent writes {{/pages/billing}}
  workspacePage("billing"),
  // URL /emails; agent writes {{/pages/emails}}
  workspacePage("emails"),
  // URL /account; agent writes {{/pages/account}}
  z.object({ page: z.literal("account") }),
]);

export type PageTarget = z.infer<typeof pageTargetSchema>;
